import {
  Button,
  Dropdown,
  Input,
  Modal,
  Empty,
  Select,
  Switch,
} from "antd";
import { useMemo, useState } from "react";
import {
  DownOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ApiSavedViews from "../../api/saved-views";
import useNotification from "../notification/notification";
import { Icon } from "../icon/icon";
import DropdownFilters from "../dropdown-filters/dropdown-filters";
import "./saved-views-control.css";

const normalizePrimitiveArray = (value) => {
  if (!Array.isArray(value)) return [];

  return Array.from(
    new Set(
      value
        .filter((item) => item !== null && item !== undefined && item !== "")
        .map((item) => String(item))
    )
  ).sort();
};

const normalizePeriod = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .filter((item) => item && (item.start || item.end))
    .map((item) => ({
      start: item.start || null,
      end: item.end || null,
    }));
};

const normalizeFiltersForCompare = (filters) => {
  if (!filters || typeof filters !== "object" || Array.isArray(filters)) {
    return {};
  }

  const result = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    if (key === "period") {
      const normalized = normalizePeriod(value);
      if (normalized.length) result[key] = normalized;
      return;
    }

    if (Array.isArray(value)) {
      const normalized = normalizePrimitiveArray(value);
      if (normalized.length) result[key] = normalized;
      return;
    }

    if (typeof value === "object") {
      const nested = normalizeFiltersForCompare(value);
      if (Object.keys(nested).length) result[key] = nested;
      return;
    }

    if (value !== "") result[key] = value;
  });

  return result;
};

const stableStringify = (value) =>
  JSON.stringify(normalizeFiltersForCompare(value || {}));

const normalizeSorting = (sorting) => ({
  sortField: sorting?.sortField || null,
  sortOrder: sorting?.sortOrder || null,
});

const stableSortingStringify = (sorting) =>
  JSON.stringify(normalizeSorting(sorting));

const sortViews = (list = []) =>
  [...list].sort((a, b) => {
    if (Boolean(a.isDefault) !== Boolean(b.isDefault)) {
      return a.isDefault ? -1 : 1;
    }

    return String(a.name || "").localeCompare(String(b.name || ""));
  });

const LABEL_STYLE = {
  width: 86,
  minWidth: 86,
  color: "#6C6C89",
  fontFamily: "Sora",
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: 14,
  lineHeight: "140%",
  letterSpacing: "0.1px",
  display: "flex",
  alignItems: "center",
  minHeight: 32,
};

const ROW_STYLE = {
  display: "flex",
  alignItems: "flex-start",
  gap: 16,
};

const RIGHT_COL_STYLE = {
  flex: 1,
  minWidth: 0,
};

const selectClassName = "saved-views-control__select";
const modalFiltersClassName = "saved-views-control__modal-filters";
const titleInputClassName = "saved-views-control__title-input";

const SavedViewsControl = ({
  projectId,
  scope,
  filters,
  sorting,
  defaultFilters = {},
  defaultSorting = { sortField: null, sortOrder: null },
  filterItems = [],
  sortingOptions = [],
  savedViews = [],
  savedViewsLoading = false,
  selectedViewId,
  onSelectedViewChange,
  onApplySavedView,
  onResetFilters,
  onReloadSavedViews,
  disabled = false,
}) => {
  const { openNotification } = useNotification();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const [modalMode, setModalMode] = useState("create");
  const [viewName, setViewName] = useState("");
  const [viewIsDefault, setViewIsDefault] = useState(false);
  const [modalFilters, setModalFilters] = useState({});
  const [modalSorting, setModalSorting] = useState({
    sortField: null,
    sortOrder: null,
  });

  const selectedView = useMemo(
    () => savedViews.find((item) => item.id === selectedViewId) || null,
    [savedViews, selectedViewId]
  );

  // const hasSavedViews = savedViews.length > 0;

  // const hasActiveFilters = useMemo(() => {
  //   const current = stableStringify(filters);
  //   const defaultState = stableStringify(defaultFilters);
  //   const emptyState = stableStringify({});

  //   return current !== defaultState && current !== emptyState;
  // }, [filters, defaultFilters]);

  const isModified = useMemo(() => {
    if (!selectedView) return false;

    const filtersChanged =
      stableStringify(selectedView.filters) !== stableStringify(filters);

    const sortingChanged =
      stableSortingStringify(selectedView.sorting) !==
      stableSortingStringify(sorting);

    return filtersChanged || sortingChanged;
  }, [selectedView, filters, sorting]);

  const shouldShowControl = true;

  const filteredViews = useMemo(() => {
    const query = String(searchQuery || "").trim().toLowerCase();

    if (!query) return sortViews(savedViews);

    return sortViews(savedViews).filter((view) =>
      String(view.name || "").toLowerCase().includes(query)
    );
  }, [savedViews, searchQuery]);

  const notifySuccess = (text) => {
    openNotification?.({ type: "success", message: <p>{text}</p> });
  };

  const notifyError = (text) => {
    openNotification?.({ type: "error", message: <p>{text}</p> });
  };

  const notifyWarning = (text) => {
    openNotification?.({ type: "warning", message: <p>{text}</p> });
  };

  const handleApplyView = (view) => {
    onSelectedViewChange?.(view.id);
    onApplySavedView?.(
      {
        filters: view.filters || {},
        sorting: normalizeSorting(view.sorting),
      },
      view.id
    );
    setDropdownOpen(false);
  };

  const handleClearSelectedView = (e) => {
    e?.stopPropagation?.();
    onSelectedViewChange?.(null);
    onResetFilters?.();
    setDropdownOpen(false);
  };

  const openCreateModal = () => {
    setModalMode("create");
    setViewName("");
    setViewIsDefault(false);
    setModalFilters(filters || {});
    setModalSorting(normalizeSorting(sorting));
    setIsViewModalOpen(true);
    setDropdownOpen(false);
  };

  const openEditModal = () => {
    if (!selectedView) return;

    setModalMode("edit");
    setViewName(selectedView.name || "");
    setViewIsDefault(!!selectedView.isDefault);
    setModalFilters(filters || {});
    setModalSorting(normalizeSorting(sorting));
    setIsViewModalOpen(true);
    setDropdownOpen(false);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setIsDeleteConfirmOpen(false);
    setViewName("");
    setViewIsDefault(false);
    setModalFilters({});
    setModalSorting({ sortField: null, sortOrder: null });
  };

  const handleOpenDeleteConfirm = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setIsDeleteConfirmOpen(false);
  };

  const handleDeleteView = async () => {
    if (!selectedView?.id) return;

    try {
      const deletingSelected = selectedViewId === selectedView.id;

      await ApiSavedViews.deleteSavedView(selectedView.id);
      await onReloadSavedViews?.();

      if (deletingSelected) {
        onSelectedViewChange?.(null);
        onResetFilters?.();
      }

      setIsDeleteConfirmOpen(false);
      closeViewModal();

      notifySuccess("View deleted");
    } catch (error) {
      console.error("Failed to delete view", error);
      notifyError("Failed to delete view");
    }
  };

  const handleSaveView = async () => {
    const trimmedName = viewName.trim();

    if (!trimmedName) {
      notifyWarning("Enter view name");
      return;
    }

    const payload = {
      projectId,
      scope,
      name: trimmedName,
      filters: modalFilters,
      sorting: normalizeSorting(modalSorting),
      isDefault: viewIsDefault,
    };

    try {
      if (modalMode === "create") {
        const { data } = await ApiSavedViews.createSavedView(payload);
        const created = data?.savedView;

        await onReloadSavedViews?.();
        onSelectedViewChange?.(created?.id || null);
        onApplySavedView?.(
          {
            filters: created?.filters || modalFilters,
            sorting: normalizeSorting(created?.sorting || modalSorting),
          },
          created?.id || null
        );

        notifySuccess("View created");
      } else {
        const { data } = await ApiSavedViews.updateSavedView(selectedView.id, {
          name: trimmedName,
          filters: modalFilters,
          sorting: normalizeSorting(modalSorting),
          isDefault: viewIsDefault,
        });

        const updated = data?.savedView;

        await onReloadSavedViews?.();
        onSelectedViewChange?.(updated?.id || selectedView.id);
        onApplySavedView?.(
          {
            filters: updated?.filters || modalFilters,
            sorting: normalizeSorting(updated?.sorting || modalSorting),
          },
          updated?.id || selectedView.id
        );

        notifySuccess("View updated");
      }

      closeViewModal();
    } catch (error) {
      console.error("Failed to save view", error);

      if (error?.response?.status === 409) {
        notifyError("View name already exists");
        return;
      }

      notifyError("Failed to save view");
    }
  };

  if (!shouldShowControl) {
    return null;
  }

  const popupContent = (
    <div
      style={{
        width: 384,
        maxWidth: "calc(100vw - 80px)",
        height: 282,
        borderRadius: 8,
        background: "#FFFFFF",
        boxShadow: "0px 4px 116px 0px #00000026",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          height: 47,
          padding: "0 8px",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Find"
          prefix={<Icon name={"search"} color={"text-1"} />}
          variant="borderless"
          style={{ height: 32 }}
        />
      </div>

      <div style={{ height: 1, background: "rgba(108, 108, 137, 0.12)" }} />

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 8,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {filteredViews.length ? (
          filteredViews.map((view) => {
            const isSelected = selectedViewId === view.id;

            return (
              <div
                key={view.id}
                onClick={() => handleApplyView(view)}
                role="presentation"
                style={{
                  width: "100%",
                  minHeight: 40,
                  padding: "7px 12px",
                  borderRadius: 8,
                  background: isSelected ? "#6C6C8912" : "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    minWidth: 0,
                    flex: 1,
                    fontSize: 14,
                    color: "#1F1F29",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: 400,
                  }}
                >
                  {view.name}
                </div>

                {isSelected ? (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={handleClearSelectedView}
                    style={{
                      border: "none",
                      boxShadow: "none",
                      color: "#6C6C89",
                      width: 20,
                      minWidth: 20,
                      height: 20,
                      padding: 0,
                    }}
                  />
                ) : null}
              </div>
            );
          })
        ) : (
          <div style={{ paddingTop: 24 }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No views found"
            />
          </div>
        )}
      </div>

      <div style={{ height: 1, background: "rgba(108, 108, 137, 0.12)" }} />

      <div
        style={{
          height: 47,
          padding: "0 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <Button
          type="text"
          disabled={disabled || !selectedView}
          onClick={openEditModal}
          style={{
            height: 32,
            border: "none",
            boxShadow: "none",
            background: "#FFFFFF",
            color: "#6C6C89",
            borderRadius: 8,
            fontWeight: 500,
            paddingInline: 12,
          }}
        >
          Edit current
        </Button>

        <Button
          type="text"
          disabled={disabled}
          onClick={openCreateModal}
          style={{
            height: 32,
            border: "none",
            boxShadow: "none",
            background: "#6C6C8912",
            color: "#6C6C89",
            borderRadius: 8,
            fontWeight: 500,
            paddingInline: 12,
          }}
        >
          Create new
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Dropdown
        trigger={["click"]}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        popupRender={() => popupContent}
        disabled={disabled || savedViewsLoading}
      >
        <div
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <Button
            style={{
              background: "var(--bg-layout-color)",
              color: "rgba(108, 108, 137, 1)",
              border: "none",
              boxShadow: "none",
              height: "32px",
              fontWeight: 700,
            }}
          >
            {selectedView?.name || "Saved views"} <DownOutlined />
          </Button>

          {selectedView && isModified ? (
            <span
              style={{
                position: "absolute",
                top: -2,
                right: -2,
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#279EF5",
                pointerEvents: "none",
              }}
            />
          ) : null}
        </div>
      </Dropdown>

      <Modal
        title={null}
        open={isViewModalOpen}
        onCancel={closeViewModal}
        footer={null}
        width={640}
        className="saved-view-modal"
        styles={{
          body: {
            padding: 0,
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            background: "#FFFFFF",
          }}
        >
          <div
            style={{
              height: 56,
              padding: "0 24px",
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid rgba(108, 108, 137, 0.12)",
              fontSize: 18,
              fontWeight: 600,
              color: "#1F1F29",
              flexShrink: 0,
            }}
          >
            {modalMode === "edit" ? "Edit view" : "Create new view"}
          </div>

          <div
            style={{
              padding: "20px 24px",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 560,
                display: "flex",
                flexDirection: "column",
                gap: 18,
              }}
            >
              <div style={ROW_STYLE}>
                <div style={LABEL_STYLE}>Name</div>
                <div style={RIGHT_COL_STYLE}>
                  <Input
                    value={viewName}
                    onChange={(e) => setViewName(e.target.value)}
                    maxLength={120}
                    placeholder="Enter view name"
                    variant="borderless"
                    className={titleInputClassName}
                  />
                </div>
              </div>
              
              <div style={ROW_STYLE}>
                <div style={LABEL_STYLE}>Sort</div>

                <div style={RIGHT_COL_STYLE}>
                  <div className="saved-views-sort-selects">
                    <Select
                      value={modalSorting.sortField}
                      onChange={(value) =>
                        setModalSorting((prev) => ({ ...prev, sortField: value }))
                      }
                      style={{ width: 200 }}
                      placeholder="Sort field"
                      options={sortingOptions}
                      allowClear
                      className={selectClassName}
                      variant="borderless"
                    />

                    <Select
                      value={modalSorting.sortOrder}
                      onChange={(value) =>
                        setModalSorting((prev) => ({ ...prev, sortOrder: value }))
                      }
                      style={{ width: 148 }}
                      className={selectClassName}
                      placeholder="Sort order"
                      options={[
                        { value: "ASC", label: "Ascending" },
                        { value: "DESC", label: "Descending" },
                      ]}
                      allowClear
                      variant="borderless"
                    />
                  </div>
                </div>
              </div>

              <div style={ROW_STYLE}>
                <div style={LABEL_STYLE}>Is default</div>
                <div style={RIGHT_COL_STYLE}>
                  <Switch checked={viewIsDefault} onChange={setViewIsDefault} />
                </div>
              </div>

              <div style={{ ...ROW_STYLE, alignItems: "flex-start" }}>
                <div style={LABEL_STYLE}>Filters</div>
                <div style={RIGHT_COL_STYLE}>
                  <DropdownFilters
                    filtersBy={filterItems}
                    updateFilterValue={setModalFilters}
                    filter={modalFilters}
                    mode="controlled"
                    solidBorder={false}
                    addButtonLabel="+ Add"
                    className={modalFiltersClassName}
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              height: 64,
              padding: "12px 24px 12px 0px",
              display: "flex",
              alignItems: "center",
              justifyContent: modalMode === "edit" ? "space-between" : "flex-end",
              gap: 12,
              borderTop: "1px solid rgba(108, 108, 137, 0.12)",
              flexShrink: 0,
            }}
          >
            {modalMode === "edit" ? (
              <Button
                danger
                type="text"
                style={{
                  height: 40,
                  paddingInline: 10,
                  marginLeft: 10
                }}
                onClick={handleOpenDeleteConfirm}
              >
                Delete view
              </Button>
            ) : (
              <div />
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
              <Button
                className="assets-header__cancel-btn"
                style={{
                  background: "rgba(108, 108, 137, 0.08)",
                  height: 40,
                  paddingInline: 20,
                  margin: 0
                }}
                onClick={closeViewModal}
              >
                Cancel
              </Button>

              <Button
                type="primary"
                className="assets-header__add-btn"
                style={{
                  height: 40,
                  paddingInline: 20,
                  marginLeft: 8
                }}
                onClick={handleSaveView}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title="Delete view"
        open={isDeleteConfirmOpen}
        onCancel={handleCloseDeleteConfirm}
        onOk={handleDeleteView}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this view?</p>
      </Modal>

      <style>
        {`
          .${selectClassName} .ant-select-selector {
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
            background: transparent !important;
            padding-left: 0 !important;
          }

          .${selectClassName}.ant-select-focused .ant-select-selector,
          .${selectClassName}.ant-select-open .ant-select-selector,
          .${selectClassName}:hover .ant-select-selector {
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
          }

          .${titleInputClassName} .ant-input,
          .${titleInputClassName}.ant-input {
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }

          .${titleInputClassName} .ant-input:focus,
          .${titleInputClassName} .ant-input-focused,
          .${titleInputClassName}.ant-input:focus,
          .${titleInputClassName}.ant-input-focused {
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
          }

          .${modalFiltersClassName} button .anticon + span,
          .${modalFiltersClassName} button span + span {
            font-size: 0 !important;
          }

          .${modalFiltersClassName} button {
            min-width: auto !important;
          }

          .${modalFiltersClassName} button::after {
            content: "+ Add";
            font-size: 14px;
          }
        `}
      </style>
    </>
  );
};

export default SavedViewsControl;